import type { Memo } from '@memori-web/shared';

export function calculateLevel(count: number): 0 | 1 | 2 | 3 | 4 {
  if (count === 0) return 0;
  if (count === 1) return 1;
  if (count <= 3) return 2;
  if (count <= 5) return 3;
  return 4;
}

export function daysInMonth(year: number, month1based: number): number {
  return new Date(year, month1based, 0).getDate();
}

export interface ForestGridCallbacks {
  onSelectDay: (date: string) => void;
}

export function renderForestGrid(
  root: HTMLElement,
  memos: Memo[],
  year: number,
  month: number, // 1-12
  selectedDate: string,
  todayDate: string,
  cb: ForestGridCallbacks,
) {
  const counts = new Map<string, number>();
  for (const m of memos.filter(x => !x.deletedAt)) {
    counts.set(m.date, (counts.get(m.date) ?? 0) + 1);
  }
  const totalDays = daysInMonth(year, month);
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay(); // 0 = 日
  const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

  // 月初までの空セル
  const cells: string[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    cells.push('<span class="forest-grid__cell forest-grid__cell--empty" aria-hidden="true"></span>');
  }
  // 日付セル
  for (let d = 1; d <= totalDays; d++) {
    const date = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const lvl = calculateLevel(counts.get(date) ?? 0);
    const dayOfWeek = (firstDayOfWeek + d - 1) % 7;
    const classes = [
      'forest-grid__cell',
      `forest-grid__cell--l${lvl}`,
      dayOfWeek === 0 ? 'is-sun' : '',
      dayOfWeek === 6 ? 'is-sat' : '',
      date === todayDate ? 'is-today' : '',
      date === selectedDate ? 'is-selected' : '',
    ].filter(Boolean).join(' ');
    cells.push(`<button class="${classes}" data-date="${date}" aria-label="${date} ${counts.get(date) ?? 0} 件"><span class="forest-grid__day">${d}</span></button>`);
  }

  const weekdayHeaders = WEEKDAYS.map((w, i) => {
    const cls = i === 0 ? 'is-sun' : i === 6 ? 'is-sat' : '';
    return `<span class="forest-grid__weekday ${cls}">${w}</span>`;
  }).join('');

  const monthLabel = `${year} 年 ${month} 月`;
  root.innerHTML = `
    <div class="forest-grid">
      <div class="forest-grid__nav">
        <button type="button" class="forest-grid__nav-btn" data-action="prev" aria-label="前の月">‹</button>
        <span class="forest-grid__month">${monthLabel}</span>
        <div class="forest-grid__nav-right">
          <button type="button" class="forest-grid__nav-btn" data-action="next" aria-label="次の月">›</button>
          <button type="button" class="forest-grid__nav-btn" data-action="year" aria-label="1 年の森を表示">📈</button>
        </div>
      </div>
      <div class="forest-grid__weekdays" aria-hidden="true">${weekdayHeaders}</div>
      <div class="forest-grid__cells" role="grid">${cells.join('')}</div>
    </div>
  `;
  root.querySelectorAll<HTMLButtonElement>('.forest-grid__cell:not(.forest-grid__cell--empty)').forEach(btn => {
    btn.addEventListener('click', () => cb.onSelectDay(btn.dataset.date!));
  });
}
